import { Component, HostListener, ViewChild } from '@angular/core';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { BaseState, LoginParams } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { SysGlobals } from '@core/framework/sysglobals';
import {
    EnumLanguages, EnumLoginExtensionLinkType, EnumLoginSecureKeyTypes,
    EnumNotificationDeliveryOptions, EnumCheckCredentialsResult
} from '@core/framework/sysenums.enum';
import { EnumSystemOptions } from '@core/framework/sysenums.enum';
import { LoginService } from '@core/framework/login.service';
import { LoginExtensionLink } from '@base/classes/BaseClasses';
import { VersionDescriptor } from '@m4u-angular/configuration/app.config.service';
import { LanguageService } from '@m4u-angular/core/framework/language.service';
import { Subscription } from 'rxjs';
import { SessionService } from '@m4u-angular/core/framework/session.service';
import { LocalStorageService } from '@m4u-angular/core/framework/local-storage.service';

class State extends BaseState {
    IsSaveCredentials: boolean;
    ShowPasswordRecoveryLink: boolean;
    LoginExtensionLinks: LoginExtensionLink[];
    ReturnUrl: string;
    IsSupportedLoginByOTP: boolean;
    Stage: number;
    ModulesVersion: VersionDescriptor[] = [];
    SessionID: string;
    DisplayName: string;
    UserName: string;
    Password: string;
    MFACode: string;
    SecureKeyType: EnumLoginSecureKeyTypes = EnumLoginSecureKeyTypes.Password;
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BasePageComponent<LoginParams, State> {
    @HostListener('document:keypress', ['$event'])
    EnterPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.OnSubmit();
        }
    }

    @ViewChild('validationGroup') protected validationGroup: DxValidationGroupComponent;

    get IsOtpButtonVisible(): boolean {
        return this.State.IsSupportedLoginByOTP && !this.IsUseSessionCredentials;
    }

    get IsUseSessionCredentials(): boolean {
        return !!this.State.SessionID && !!this.State.DisplayName;
    }

    private _subscriptions: Subscription[] = [];

    constructor(A: ServiceBagService,
        private loginService: LoginService,
        private sessionService: SessionService,
        private localStorageService: LocalStorageService,
        private languageService: LanguageService) {
        super(A, 'LoginComponent', LoginParams, State);
    }

    override OnInitComponent() {
        this.A.AppConfigService.AppConfig.OnLoginPage();

        //If authentication is set to OAuth - redirec to a separate page
        if (this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByOAuth)) {
            this.Navigate(['/system/login-oauth'], { Language: SysGlobals.Language, ReturnUrl: this.State.ReturnUrl });
            return;
        }

        //Checking support of the login by otp is needed for to avoid an endless loop of transitions between 'login' & login-otp'
        if (!this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByPassword) && this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByOTP)) {
            this.Navigate(['/system/login-otp'], { Language: SysGlobals.Language, ReturnUrl: this.State.ReturnUrl });
            return;
        }

        this.State.Stage = 1;

        this.State.ModulesVersion = this.A.AppConfigService.getModulesVersion();

        this._subscriptions.push(this.languageService.Language$.subscribe(language => {
            if (this.Entity.Language === language) return;

            this.Entity.Language = language;
            this.A.Navigator.Navigate(['/system/login'], { Language: SysGlobals.Language });
            setTimeout(() => this.A.Navigator.ReloadApplication(), 500);
        }));
    }

    override OnInitState() {
        this.State.ShowPasswordRecoveryLink = this.SystemOptionFlag(EnumSystemOptions.IsSupportedPasswordRecovery);
        this.State.IsSupportedLoginByOTP = this.SystemOptionFlag(EnumSystemOptions.IsSupportedLoginByOTP);
        this.State.ReturnUrl = this.A.Navigator.FixDeploymentLocation(this.QueryParams.ReturnUrl);
        
        this.State.DisplayName = this.sessionService.GetUserName();
        this.State.SessionID = this.sessionService.GetSessionID();
        
        if(this.IsUseSessionCredentials) {
            this.State.SecureKeyType = EnumLoginSecureKeyTypes.Session;
        }
    }

    override OnInitEntity() {
        //Take default language from URL to allow presetting it
        if (this.QueryParams.Language != undefined) {
            this.Entity.Language = this.QueryParams.Language;
        } else {
            const language = this.localStorageService.GetItem('Language');
            this.Entity.Language = language || this.A.AppConfigService.M4UConfig.defaultLanguage;
        }
        this.languageService.SetLanguage(this.Entity.Language);

        //Take sure if was passed in
        this.State.UserName = this.QueryParams.UserName;
        this.LoadExtension();
    }

    //Perform the login
    // We can login by either by session credentials or by username and password
    OnSubmit() {
        if (!this.validationGroup.instance.validate().isValid) return;

        this.PrepareEntity();

        this.loginService.DoLogin(this.Entity, this.IsUseSessionCredentials, this.State.IsSaveCredentials, 
            () => {
                this.OnAfterInit(); 
            }, 
            (failedResponse) => {
                if(failedResponse.ResponseCode != 999 && this.IsUseSessionCredentials) {
                    this.ClearSavedSessionState();
                    this.Entity.SecureKey = undefined;
                }
            }, 
            (deliveryOption: EnumNotificationDeliveryOptions) => {
                this.MFACodeSent(deliveryOption); 
            })
            .subscribe(() => {});
    }

    OnAfterInit() {
        if (this.State.ReturnUrl) {
            this.A.Navigator.NavigateByUrl(this.State.ReturnUrl);
            return;
        }
        this.A.Framework.NavigateDefault();
    }

    override OnSubmitPage() {
        this.OnSubmit();
    }

    OnPasswordRecovery() {
        this.Navigate(['/system/passwordrecovery'], { UserName: this.State.UserName });
    }

    LoadExtension() {
        this.A.AppConfigService.AppConfig.OnGetAppLoginExtensionLinks()
            .subscribe(links => this.State.LoginExtensionLinks = links);
    }

    OnExtensionLinkClick(link: LoginExtensionLink) {
        (link.Type === EnumLoginExtensionLinkType.Route || link.Type === EnumLoginExtensionLinkType.ButtonLink)
            && this.Navigate([link.Link]);
    }

    GoToLoginByOTP() {
        if (this.State.IsSupportedLoginByOTP) this.Navigate(['/system/login-otp'], { Language: this.Entity.Language });
    }

    MFACodeSent(deliveryOption: EnumNotificationDeliveryOptions) {
        this.Entity.DeliveryOption = deliveryOption;
        this.State.Stage = 2;
        this.State.IsSaveCredentials = false;
        this.State.SecureKeyType = EnumLoginSecureKeyTypes.MFACode;

        const message = this.Entity.DeliveryOption == EnumNotificationDeliveryOptions.Email
            ? this.RC('titleMFACodeOnEmailSent')
            : this.RC('titleMFACodeOnPhoneSent');
        this.UI.InfoMessage(message);
    }

    GetAdditionalTitle() {
        if (this.State.Stage == 2) {
            if (this.Entity.DeliveryOption == EnumNotificationDeliveryOptions.Email) return this.RC('titleMFACodeOnEmailSent');
            else return this.RC('titleMFACodeOnPhoneSent');
        }
        return '';
    }

    OnCodeFieldInitialized(e) {
        setTimeout(() => e.component.instance().focus(), 500);
    }

    OnClearSavedSession() {
        this.ClearSavedSessionState();
    }

    protected override OnDestroyComponent(): void {
        this._subscriptions.forEach(x => x.unsubscribe());
    }

    private ClearSavedSessionState() {
        this.sessionService.ClearSessionCredentials();
        this.State.DisplayName = undefined;
        this.State.SessionID = undefined;
        this.State.SecureKeyType = EnumLoginSecureKeyTypes.Password;
    }

    private PrepareEntity() {
        this.Entity.SecureKeyType = this.State.SecureKeyType;
        this.Entity.UserName = this.State.UserName;
        
        if(this.IsUseSessionCredentials) {
            this.Entity.SecureKey = this.State.SessionID;
            // When we use session ID to log in we'd like to keep it in the storage till user explicitly logs out or this sessionID is expired
            this.State.IsSaveCredentials = true;
            return;
        }

        if(this.State.SecureKeyType == EnumLoginSecureKeyTypes.Password) {
            this.Entity.SecureKey = this.State.Password;
            return;
        }

        this.Entity.SecureKey = this.State.MFACode;
    }
}
